import { useEffect, useState } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/base/Breadcrumb';
import { aboutInfo } from '@/mocks/about';
import { faqItems } from '@/mocks/faq';
import useSEO from '@/hooks/useSEO';

export default function AboutPage() {
  const [formUrl] = useState<string>('https://readdy.ai/api/form/d9u4qkd3pcjqs2fcsm80');
  const [formStatus, setFormStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');
  const [faqOpenIndex, setFaqOpenIndex] = useState<number | null>(null);

  const aboutSchema = {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'AboutPage',
        name: '关于掌中之物',
        description: aboutInfo.description,
        url: 'https://valuablespot.github.io/about',
        breadcrumb: {
          '@type': 'BreadcrumbList',
          itemListElement: [
            { '@type': 'ListItem', position: 1, name: '首页', item: 'https://valuablespot.github.io' },
            { '@type': 'ListItem', position: 2, name: '关于我们' },
          ],
        },
        mainEntity: {
          '@type': 'Organization',
          name: '掌中之物',
          description: aboutInfo.description,
          foundingDate: String(aboutInfo.founded),
          address: {
            '@type': 'PostalAddress',
            streetAddress: aboutInfo.contact.address,
            addressLocality: '北京',
            addressCountry: 'CN',
          },
          telephone: aboutInfo.contact.phone,
          email: aboutInfo.contact.email,
        },
      },
      {
        '@type': 'FAQPage',
        mainEntity: faqItems.map((item) => ({
          '@type': 'Question',
          name: item.question,
          acceptedAnswer: {
            '@type': 'Answer',
            text: item.answer,
          },
        })),
      },
    ],
  };

  useSEO({
    title: '关于我们 - 品牌介绍与联系方式',
    description: `${aboutInfo.description} 了解掌中之物的品牌故事、核心理念、口碑评价以及联系方式。`,
    keywords: '掌中之物品牌,掌中之物关于我们,掌中之物联系方式,掌中之物口碑',
    canonicalUrl: 'https://valuablespot.github.io/about',
    schema: aboutSchema,
  });

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const handleFormSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!formUrl) return;

    const form = e.currentTarget;
    const honeypot = form.querySelector<HTMLInputElement>('input[name="phone_alt"]');
    if (honeypot && honeypot.value.trim() !== '') {
      setFormStatus('success');
      form.reset();
      setTimeout(() => setFormStatus('idle'), 3000);
      return;
    }

    setFormStatus('loading');
    try {
      const formData = new FormData(form);
      if (honeypot) formData.delete('phone_alt');
      const response = await fetch(formUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });
      const responseText = await response.text();
      let parsed;
      try { parsed = JSON.parse(responseText); } catch { parsed = null; }
      if (response.ok && parsed?.code === 'OK') {
        setFormStatus('success');
        form.reset();
        setTimeout(() => setFormStatus('idle'), 3000);
      } else {
        const msg = parsed?.meta?.message || parsed?.message || responseText || '提交失败';
        setFormError(msg);
        setFormStatus('error');
      }
    } catch {
      setFormError('网络错误，请稍后重试');
      setFormStatus('error');
    }
  };

  const StarRating = ({ rating }: { rating: number }) => (
    <div className="flex gap-0.5">
      {Array.from({ length: 5 }).map((_, i) => (
        <i
          key={i}
          className={`text-xs ${i < rating ? 'ri-star-fill text-primary-500' : 'ri-star-line text-foreground-300'}`}
        ></i>
      ))}
    </div>
  );

  return (
    <>
      <Navbar />
      <main className="pt-16 md:pt-20">
        <div className="max-w-7xl mx-auto px-4 md:px-6 py-6">
          <Breadcrumb
            items={[
              { label: '首页', href: '/' },
              { label: '关于我们' },
            ]}
          />
        </div>

        <section className="py-10 md:py-16 px-4 md:px-6 bg-background-100">
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-10">
              <h1 className="text-3xl md:text-4xl font-heading font-bold text-foreground-900 mb-3">
                {aboutInfo.brandName}
              </h1>
              <p className="text-lg md:text-xl text-primary-600 font-heading mb-2">{aboutInfo.tagline}</p>
              <p className="text-sm text-foreground-400">成立于 {aboutInfo.founded} 年</p>
            </div>
            <div className="max-w-3xl mx-auto">
              <p className="text-sm md:text-base text-foreground-600 leading-relaxed text-center mb-8">
                {aboutInfo.description}
              </p>
              <div className="aspect-[16/7] w-full rounded-lg overflow-hidden">
                <img
                  src="https://readdy.ai/api/search-image?query=Elegant%20Chinese%20traditional%20artisan%20workshop%20with%20various%20handicrafts%20jade%20carvings%20and%20tea%20sets%20displayed%20on%20wooden%20shelves%2C%20warm%20natural%20lighting%2C%20artistic%20atmosphere%2C%20cream%20walls%2C%20professional%20interior%20photography&width=1200&height=525&seq=about-hero-01&orientation=landscape"
                  alt="掌中之物品牌形象"
                  className="w-full h-full object-cover object-top"
                />
              </div>
            </div>
          </div>
        </section>

        <section className="py-10 md:py-16 px-4 md:px-6">
          <div className="max-w-7xl mx-auto">
            <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900 text-center mb-10">
              品牌理念
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {aboutInfo.values.map((v) => (
                <div key={v.title} className="bg-background-50 border border-background-200/70 rounded-lg p-6 text-center">
                  <div className="w-12 h-12 flex items-center justify-center rounded-full bg-primary-100 text-primary-600 mx-auto mb-4">
                    <i className={`${v.icon} text-xl`}></i>
                  </div>
                  <h3 className="text-base font-heading font-semibold text-foreground-800 mb-2">{v.title}</h3>
                  <p className="text-sm text-foreground-500 leading-relaxed">{v.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="py-10 md:py-16 px-4 md:px-6 bg-background-100">
          <div className="max-w-7xl mx-auto">
            <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900 text-center mb-10">
              口碑见证
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {aboutInfo.reputation.map((r, i) => (
                <div key={i} className="bg-background-50 border border-background-200/70 rounded-lg p-5">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-10 h-10 rounded-full bg-secondary-200 flex items-center justify-center text-foreground-700 font-heading font-semibold text-sm">
                      {r.name[0]}
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-foreground-800">{r.name}</p>
                      <p className="text-xs text-foreground-400">{r.title}</p>
                    </div>
                    <div className="ml-auto">
                      <StarRating rating={r.rating} />
                    </div>
                  </div>
                  <p className="text-sm text-foreground-600 leading-relaxed">{r.content}</p>
                  <p className="text-xs text-foreground-400 mt-3">{r.date}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="py-10 md:py-16 px-4 md:px-6">
          <div className="max-w-7xl mx-auto">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-10">
              <div>
                <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900 mb-6">
                  联系我们
                </h2>
                <div className="space-y-4 mb-8">
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-primary-100 text-primary-600 shrink-0">
                      <i className="ri-map-pin-line text-lg"></i>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-foreground-800">地址</p>
                      <p className="text-sm text-foreground-500">{aboutInfo.contact.address}</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-primary-100 text-primary-600 shrink-0">
                      <i className="ri-phone-line text-lg"></i>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-foreground-800">电话</p>
                      <p className="text-sm text-foreground-500">{aboutInfo.contact.phone}</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-primary-100 text-primary-600 shrink-0">
                      <i className="ri-mail-line text-lg"></i>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-foreground-800">邮箱</p>
                      <p className="text-sm text-foreground-500">{aboutInfo.contact.email}</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-primary-100 text-primary-600 shrink-0">
                      <i className="ri-wechat-line text-lg"></i>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-foreground-800">微信</p>
                      <p className="text-sm text-foreground-500">{aboutInfo.contact.wechat}</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-primary-100 text-primary-600 shrink-0">
                      <i className="ri-time-line text-lg"></i>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-foreground-800">营业时间</p>
                      <p className="text-sm text-foreground-500">{aboutInfo.contact.businessHours}</p>
                    </div>
                  </div>
                </div>
              </div>

              <div>
                <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900 mb-6">
                  在线留言
                </h2>
                {formUrl ? (
                  <form onSubmit={handleFormSubmit} data-readdy-form id="contact-form" className="space-y-4">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <label htmlFor="contact-name" className="block text-sm font-medium text-foreground-700 mb-1.5">
                          姓名 <span className="text-accent-500">*</span>
                        </label>
                        <input
                          type="text"
                          id="contact-name"
                          name="name"
                          required
                          className="w-full px-3 py-2.5 rounded-lg border border-background-300 bg-background-50 text-sm text-foreground-800 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-colors"
                          placeholder="请输入您的姓名"
                        />
                      </div>
                      <div>
                        <label htmlFor="contact-email" className="block text-sm font-medium text-foreground-700 mb-1.5">
                          邮箱 <span className="text-accent-500">*</span>
                        </label>
                        <input
                          type="email"
                          id="contact-email"
                          name="email"
                          required
                          className="w-full px-3 py-2.5 rounded-lg border border-background-300 bg-background-50 text-sm text-foreground-800 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-colors"
                          placeholder="请输入您的邮箱"
                        />
                      </div>
                    </div>
                    <div>
                      <label htmlFor="contact-subject" className="block text-sm font-medium text-foreground-700 mb-1.5">
                        主题
                      </label>
                      <input
                        type="text"
                        id="contact-subject"
                        name="subject"
                        className="w-full px-3 py-2.5 rounded-lg border border-background-300 bg-background-50 text-sm text-foreground-800 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-colors"
                        placeholder="请输入主题"
                      />
                    </div>
                    <div>
                      <label htmlFor="contact-message" className="block text-sm font-medium text-foreground-700 mb-1.5">
                        留言内容 <span className="text-accent-500">*</span>
                      </label>
                      <textarea
                        id="contact-message"
                        name="message"
                        required
                        maxLength={500}
                        rows={5}
                        className="w-full px-3 py-2.5 rounded-lg border border-background-300 bg-background-50 text-sm text-foreground-800 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-colors resize-none"
                        placeholder="请输入您的留言内容（限500字）"
                      ></textarea>
                    </div>
                    <input
                      type="text"
                      name="phone_alt"
                      className="honeypot-field"
                      tabIndex={-1}
                      autoComplete="off"
                      aria-hidden="true"
                      readOnly
                    />
                    {formStatus === 'error' && formError && (
                      <p className="text-sm text-accent-600">{formError}</p>
                    )}
                    {formStatus === 'success' && (
                      <p className="text-sm text-green-600">留言提交成功！我们会尽快与您联系。</p>
                    )}
                    <button
                      type="submit"
                      disabled={formStatus === 'loading'}
                      className="px-6 py-2.5 bg-primary-500 text-background-50 rounded-lg text-sm font-medium hover:bg-primary-600 transition-colors disabled:opacity-60 whitespace-nowrap cursor-pointer"
                    >
                      {formStatus === 'loading' ? '提交中...' : '提交留言'}
                    </button>
                  </form>
                ) : (
                  <div className="bg-background-50 border border-background-200/70 rounded-lg p-6 text-center">
                    <p className="text-sm text-foreground-500">表单加载中...</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </section>

        <section className="py-10 md:py-16 px-4 md:px-6 bg-background-100">
          <div className="max-w-3xl mx-auto">
            <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900 text-center mb-10">
              常见问题
            </h2>
            <div className="space-y-3">
              {faqItems.map((item, index) => (
                <div
                  key={index}
                  className="bg-background-50 border border-background-200/70 rounded-lg overflow-hidden"
                >
                  <button
                    onClick={() => setFaqOpenIndex(faqOpenIndex === index ? null : index)}
                    className="w-full flex items-center justify-between px-5 py-4 text-left cursor-pointer"
                  >
                    <span className="text-sm md:text-base font-medium text-foreground-800 pr-4">
                      {item.question}
                    </span>
                    <i
                      className={`ri-arrow-down-s-line text-lg text-foreground-400 transition-transform shrink-0 ${
                        faqOpenIndex === index ? 'rotate-180' : ''
                      }`}
                    ></i>
                  </button>
                  {faqOpenIndex === index && (
                    <div className="px-5 pb-4">
                      <p className="text-sm text-foreground-600 leading-relaxed">{item.answer}</p>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}